'use client';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { FC, useMemo, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';

import { clsx } from 'clsx';
import Image from 'next/image';
import Link from 'next/link';
import { Swiper as SwiperApi } from 'swiper/types';

import { formatBlogPostDate, getBlogPostImageUrl, getBlogPostTags, getBlogPostUrl } from '@screens/Blog';
import { BlogPostType } from '@screens/Product/model/types/blog';
import { Container } from '@shared/ui/Container';
import { SectionTitle } from '@shared/ui/SectionTitle';
import { HStack } from '@shared/ui/Stack';

import cls from './ProductBlogPosts.module.scss';

type ProductBlogPostsProps = {
  className?: string;
  posts?: BlogPostType[];
  title?: string;
};

export const ProductBlogPosts: FC<ProductBlogPostsProps> = ({
  className,
  posts = [],
  title = 'Полезные статьи и идеи для ванной комнаты'
}) => {
  const [swiper, setSwiper] = useState<SwiperApi>();
  const [canSlidePrev, setCanSlidePrev] = useState(false);
  const [canSlideNext, setCanSlideNext] = useState(false);

  const visiblePosts = useMemo(() => posts.filter(({ id, title }) => id && title), [posts]);

  const updateNavigationState = (api: SwiperApi) => {
    setCanSlidePrev(!api.isBeginning);
    setCanSlideNext(!api.isEnd);
  };

  const onClickPrev = () => {
    swiper?.slidePrev();
  };

  const onClickNext = () => {
    swiper?.slideNext();
  };

  if (!visiblePosts.length) {
    return null;
  }

  return (
    <Container className={clsx(cls.container, className)}>
      <HStack className={cls.titleWrapper}>
        <SectionTitle className={cls.sectionTitle}>{title}</SectionTitle>
        {visiblePosts.length > 1 && (
          <div className={cls.controls}>
            <button
              className={clsx(cls.control, { [cls.disable]: !canSlidePrev })}
              type="button"
              aria-label="Предыдущие статьи"
              disabled={!canSlidePrev}
              onClick={onClickPrev}
            >
              <ChevronLeft />
            </button>
            <button
              className={clsx(cls.control, { [cls.disable]: !canSlideNext })}
              type="button"
              aria-label="Следующие статьи"
              disabled={!canSlideNext}
              onClick={onClickNext}
            >
              <ChevronRight />
            </button>
          </div>
        )}
      </HStack>
      <div className={cls.sliderWrap}>
        <Swiper
          className={cls.slider}
          speed={1000}
          slidesPerView={1}
          spaceBetween={16}
          breakpoints={{
            768: {
              slidesPerView: 2,
              spaceBetween: 20
            },
            1024: {
              slidesPerView: 3,
              spaceBetween: 22
            },
            1281: {
              slidesPerView: 4,
              spaceBetween: 24
            }
          }}
          onInit={(api) => {
            setSwiper(api);
            updateNavigationState(api);
          }}
          onResize={updateNavigationState}
          onSlideChange={updateNavigationState}
        >
          {visiblePosts.map((post) => {
            const tags = getBlogPostTags(post);
            const date = formatBlogPostDate(post);
            const imageUrl = getBlogPostImageUrl(post.previewImage);

            return (
              <SwiperSlide key={post.id}>
                <Link
                  href={getBlogPostUrl(post)}
                  className={cls.card}
                >
                  <div className={cls.imageWrap}>
                    {imageUrl && (
                      <Image
                        className={cls.image}
                        src={imageUrl}
                        alt={post.previewImageAlt || post.title}
                        fill
                        sizes="(max-width: 767px) 85vw, (max-width: 1023px) 50vw, (max-width: 1280px) 33vw, 25vw"
                        unoptimized
                      />
                    )}
                    {tags.length > 0 && (
                      <div className={cls.tags}>
                        {tags.map((tag, tagIndex) => (
                          <span
                            className={cls.tag}
                            key={`${tag.id || tag.slug || tag.name}-${tagIndex}`}
                          >
                            {tag.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <h2 className={cls.title}>{post.title}</h2>
                  {date && <time className={cls.date}>{date}</time>}
                </Link>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
    </Container>
  );
};
